// Assessment API service for student panel
import { API_CONFIG } from '../config/api';
const API_BASE_URL = API_CONFIG.BASE_URL;

class AssessmentAPI {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    // Get authorization headers
    getAuthHeaders() {
        const token = localStorage.getItem('accessToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    // Test API connectivity
    async testConnection() {
        try {
            const response = await fetch(`${this.baseURL}/student/test`, {
                headers: this.getAuthHeaders()
            });
            return response.ok;
        } catch (error) {
            console.error('API connection test failed:', error);
            return false;
        }
    }

    // Get all assigned assessments for student
    async getAssignedAssessments() {
        try {
            const response = await fetch(`${this.baseURL}/student/assessments`, {
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching assigned assessments:', error);
            throw error;
        }
    }

    // Get specific assessment details
    async getAssessmentDetails(assessmentId) {
        try {
            const response = await fetch(`${this.baseURL}/student/assessments/${assessmentId}`, {
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching assessment details:', error);
            throw error;
        }
    }

    // Submit marks for an assessment
    async submitAssessmentMarks(assessmentId, questionMarks) {
        try {
            const response = await fetch(`${this.baseURL}/api/student/assessments/${assessmentId}/submit-marks`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    assessment_id: assessmentId,
                    question_marks: questionMarks
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error submitting assessment marks:', error);
            throw error;
        }
    }

    // Get student's marks for an assessment
    async getMyMarks(assessmentId) {
        try {
            const response = await fetch(`${this.baseURL}/api/student/assessments/${assessmentId}/marks`, {
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                if (response.status === 404) {
                    return { marks: null };
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching marks:', error);
            throw error;
        }
    }

    // Get all my assessments with marks status
    async getAllMyAssessments() {
        try {
            const response = await fetch(`${this.baseURL}/api/student/my-assessments`, {
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching assessments:', error);
            throw error;
        }
    }

    // Update student submission (before approval)
    async updateMyMarks(assessmentId, questionMarks) {
        try {
            const response = await fetch(`${this.baseURL}/api/student/assessments/${assessmentId}/marks`, {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    question_marks: questionMarks
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating marks:', error);
            throw error;
        }
    }

    // Legacy method for backward compatibility
    async updateSubmission(assessmentId, updateData) {
        console.warn('updateSubmission is deprecated, use updateMyMarks instead');
        return await this.updateMyMarks(assessmentId, updateData.question_marks);
    }

    // Utility functions
    formatDate(dateString) {
        if (!dateString) return 'N/A';

        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    }

    // Get status display information
    getStatusDisplay(status) {
        const statusMap = {
            'not_started': { text: 'Not Started', color: 'gray' },
            'in_progress': { text: 'In Progress', color: 'blue' },
            'submitted': { text: 'Submitted', color: 'yellow' },
            'approved': { text: 'Approved', color: 'green' },
            'revision_requested': { text: 'Needs Revision', color: 'orange' },
            'draft': { text: 'Draft', color: 'gray' }
        };

        return statusMap[status] || { text: status || 'Unknown', color: 'gray' };
    }

    // Get marks status display
    getMarksStatusDisplay(status) {
        return this.getStatusDisplay(status);
    }

    // Calculate grade from percentage
    calculateGrade(percentage) {
        if (percentage >= 90) return 'A';
        if (percentage >= 80) return 'B';
        if (percentage >= 70) return 'C';
        if (percentage >= 40) return 'D';
        return 'F';
    }

    // Validate marks input
    validateMarks(questionMarks, maxMarks) {
        const errors = [];

        questionMarks.forEach((mark, index) => {
            const obtainedMarks = mark.obtained_marks;
            const maxMark = mark.max_marks;

            if (obtainedMarks < 0) {
                errors.push(`Question ${index + 1}: Marks cannot be negative`);
            }

            if (obtainedMarks > maxMark) {
                errors.push(`Question ${index + 1}: Marks cannot exceed ${maxMark}`);
            }
        });

        return errors;
    }

    // Create question marks structure
    createQuestionMarks(assessment, marksArray) {
        if (!assessment.questions || !Array.isArray(marksArray)) {
            return [];
        }

        return assessment.questions.map((question, index) => ({
            question_id: question.id || `q_${index + 1}`,
            question_number: index + 1,
            max_marks: question.marks || 1,
            obtained_marks: marksArray[index] || 0,
            competency_id: question.competency_id || null,
            competency_name: question.competency_name || null
        }));
    }

    // Get assessment type display
    getAssessmentTypeDisplay(type) {
        const typeMap = {
            'test': 'Test',
            'quiz': 'Quiz',
            'worksheet': 'Worksheet',
            'practice': 'Practice',
            'exam': 'Exam'
        };

        return typeMap[type] || 'Assessment';
    }

    // Check if assessment is overdue
    isOverdue(dueDateString) {
        if (!dueDateString) return false;

        try {
            const dueDate = new Date(dueDateString);
            const now = new Date();
            return now > dueDate;
        } catch (error) {
            return false;
        }
    }

    // Get time remaining until due date
    getTimeRemaining(dueDateString) {
        if (!dueDateString) return null;

        try {
            const dueDate = new Date(dueDateString);
            const now = new Date();
            const diffMs = dueDate - now;

            if (diffMs <= 0) return 'Overdue';

            const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

            if (days > 0) {
                return `${days} day${days > 1 ? 's' : ''} remaining`;
            } else if (hours > 0) {
                return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
            } else {
                const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
            }
        } catch (error) {
            return null;
        }
    }

    // Export marks to CSV
    exportMarksToCSV(marks, filename = 'my_marks.csv') {
        if (!marks || marks.length === 0) {
            throw new Error('No marks data to export');
        }

        const headers = ['Assessment', 'Subject', 'Marks Obtained', 'Total Marks', 'Percentage', 'Grade', 'Status', 'Submitted Date'];
        const csvContent = [
            headers.join(','),
            ...marks.map(mark => [
                `"${mark.assessment_title || 'N/A'}"`,
                `"${mark.assessment_subject || 'N/A'}"`,
                mark.total_marks || 0,
                mark.max_total_marks || 0,
                mark.percentage || 'N/A',
                mark.grade || 'N/A',
                mark.status || 'N/A',
                this.formatDate(mark.submitted_at)
            ].join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

// Create and export singleton instance
const assessmentAPI = new AssessmentAPI();
export default assessmentAPI;